'use client';

import { useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Mask segment definition
 * - pattern: regex pattern for valid characters (e.g., /\d/ for digits)
 * - length: number of characters in this segment
 */
export interface MaskSegment {
  pattern: RegExp;
  length: number;
}

/**
 * Preset mask configurations for common formats
 */
export const MASK_PRESETS = {
  /** Australian date format: DD/MM/YYYY */
  DATE_AU: {
    segments: [
      { pattern: /\d/, length: 2 },
      { pattern: /\d/, length: 2 },
      { pattern: /\d/, length: 4 }
    ],
    separator: '/',
    placeholder: 'DD/MM/YYYY'
  },
  /** US date format: MM/DD/YYYY */
  DATE_US: {
    segments: [
      { pattern: /\d/, length: 2 },
      { pattern: /\d/, length: 2 },
      { pattern: /\d/, length: 4 }
    ],
    separator: '/',
    placeholder: 'MM/DD/YYYY'
  },
  /** Phone format: XXXX XXX XXX */
  PHONE_AU: {
    segments: [
      { pattern: /\d/, length: 4 },
      { pattern: /\d/, length: 3 },
      { pattern: /\d/, length: 3 }
    ],
    separator: ' ',
    placeholder: '0400 000 000'
  },
  /** Credit card format: XXXX XXXX XXXX XXXX */
  CREDIT_CARD: {
    segments: [
      { pattern: /\d/, length: 4 },
      { pattern: /\d/, length: 4 },
      { pattern: /\d/, length: 4 },
      { pattern: /\d/, length: 4 }
    ],
    separator: ' ',
    placeholder: '0000 0000 0000 0000'
  }
} as const;

export interface MaskConfig {
  readonly segments: readonly MaskSegment[];
  readonly separator: string;
  readonly placeholder?: string;
}

export interface MaskedInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  /** Mask configuration - use MASK_PRESETS or custom config */
  mask: MaskConfig;
  /** Current value (unformatted, just the characters) */
  value?: string;
  /** Called when value changes (receives unformatted value) */
  onChange?: (value: string) => void;
  /** Called when a complete valid value is entered */
  onComplete?: (value: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Format a raw value according to the mask (e.g., "24121987" -> "24/12/1987")
 */
function formatValue(raw: string, mask: MaskConfig): string {
  const { segments, separator } = mask;
  let result = '';
  let rawIndex = 0;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const segmentChars = raw.slice(rawIndex, rawIndex + segment.length);

    if (segmentChars.length === 0) break;

    if (i > 0) result += separator;
    result += segmentChars;
    rawIndex += segment.length;
  }

  return result;
}

/**
 * Check if a character is valid for a given raw position
 */
function isValidCharAtPosition(
  char: string,
  rawPosition: number,
  mask: MaskConfig
): boolean {
  const { segments } = mask;
  let positionInSegment = rawPosition;

  for (const segment of segments) {
    if (positionInSegment < segment.length) {
      return segment.pattern.test(char);
    }
    positionInSegment -= segment.length;
  }

  return false;
}

/**
 * Get the maximum raw length based on segments
 */
function getMaxRawLength(mask: MaskConfig): number {
  return mask.segments.reduce((sum, seg) => sum + seg.length, 0);
}

/**
 * Convert formatted cursor position to raw position
 */
function formattedToRawPosition(
  formattedPos: number,
  mask: MaskConfig
): number {
  const { segments, separator } = mask;
  let rawPos = 0;
  let formattedIndex = 0;

  for (let i = 0; i < segments.length; i++) {
    // Account for separator before this segment (except first)
    if (i > 0) {
      if (formattedPos <= formattedIndex + separator.length) {
        return rawPos;
      }
      formattedIndex += separator.length;
    }

    const segmentLength = segments[i].length;

    // Check if cursor is within this segment
    if (formattedPos <= formattedIndex + segmentLength) {
      return rawPos + (formattedPos - formattedIndex);
    }

    formattedIndex += segmentLength;
    rawPos += segmentLength;
  }

  return rawPos;
}

/**
 * Convert raw cursor position to formatted position
 */
function rawToFormattedPosition(rawPos: number, mask: MaskConfig): number {
  const { segments, separator } = mask;
  let formattedPos = 0;
  let rawConsumed = 0;

  for (let i = 0; i < segments.length; i++) {
    if (i > 0 && rawConsumed < rawPos) {
      formattedPos += separator.length;
    }

    const segmentLength = segments[i].length;
    const charsInThisSegment = Math.min(segmentLength, rawPos - rawConsumed);

    if (charsInThisSegment > 0) {
      formattedPos += charsInThisSegment;
      rawConsumed += charsInThisSegment;
    }

    if (rawConsumed >= rawPos) break;
  }

  return formattedPos;
}

/**
 * MaskedInput component
 *
 * A reusable input component that formats user input according to a mask pattern.
 * Supports various formats like dates, phone numbers, credit cards, etc.
 * Supports cursor-aware editing - users can click anywhere and edit specific digits.
 */
export const MaskedInput = ({
  ref,
  mask,
  value = '',
  onChange,
  onComplete,
  className,
  placeholder,
  ...props
}: MaskedInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayValue = useMemo(() => formatValue(value, mask), [value, mask]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const hasSelection = selectionStart !== selectionEnd;
    const maxLength = getMaxRawLength(mask);

    // Convert formatted position to raw position
    const rawCursorPos = formattedToRawPosition(selectionStart, mask);

    if (e.key === 'Backspace') {
      e.preventDefault();

      if (hasSelection) {
        // Delete selection
        const rawStart = formattedToRawPosition(selectionStart, mask);
        const rawEnd = formattedToRawPosition(selectionEnd, mask);
        const newRaw = value.slice(0, rawStart) + value.slice(rawEnd);

        onChange?.(newRaw);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newFormattedPos = rawToFormattedPosition(rawStart, mask);
            inputRef.current.setSelectionRange(
              newFormattedPos,
              newFormattedPos
            );
          }
        });
      } else if (rawCursorPos > 0) {
        // Delete character before cursor
        const newRaw =
          value.slice(0, rawCursorPos - 1) + value.slice(rawCursorPos);

        onChange?.(newRaw);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newFormattedPos = rawToFormattedPosition(
              rawCursorPos - 1,
              mask
            );
            inputRef.current.setSelectionRange(
              newFormattedPos,
              newFormattedPos
            );
          }
        });
      }
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();

      if (hasSelection) {
        // Delete selection
        const rawStart = formattedToRawPosition(selectionStart, mask);
        const rawEnd = formattedToRawPosition(selectionEnd, mask);
        const newRaw = value.slice(0, rawStart) + value.slice(rawEnd);

        onChange?.(newRaw);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newFormattedPos = rawToFormattedPosition(rawStart, mask);
            inputRef.current.setSelectionRange(
              newFormattedPos,
              newFormattedPos
            );
          }
        });
      } else if (rawCursorPos < value.length) {
        // Delete character at cursor
        const newRaw =
          value.slice(0, rawCursorPos) + value.slice(rawCursorPos + 1);

        onChange?.(newRaw);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newFormattedPos = rawToFormattedPosition(rawCursorPos, mask);
            inputRef.current.setSelectionRange(
              newFormattedPos,
              newFormattedPos
            );
          }
        });
      }
      return;
    }

    // Handle character input
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();

      const char = e.key;

      // Determine the position to insert at
      const insertPos = rawCursorPos;

      // If we have a selection, we'll replace it
      if (hasSelection) {
        const rawStart = formattedToRawPosition(selectionStart, mask);
        const rawEnd = formattedToRawPosition(selectionEnd, mask);

        // Check if char is valid at the start position
        if (isValidCharAtPosition(char, rawStart, mask)) {
          const newRaw = value.slice(0, rawStart) + char + value.slice(rawEnd);

          // Trim to max length
          const trimmedRaw = newRaw.slice(0, maxLength);

          onChange?.(trimmedRaw);

          if (trimmedRaw.length === maxLength) {
            onComplete?.(trimmedRaw);
          }

          requestAnimationFrame(() => {
            if (inputRef.current) {
              const newFormattedPos = rawToFormattedPosition(
                rawStart + 1,
                mask
              );
              inputRef.current.setSelectionRange(
                newFormattedPos,
                newFormattedPos
              );
            }
          });
        }
        return;
      }

      // No selection - insert or replace at cursor
      if (value.length < maxLength) {
        // Insert mode - add character at cursor position
        if (isValidCharAtPosition(char, insertPos, mask)) {
          const newRaw =
            value.slice(0, insertPos) + char + value.slice(insertPos);

          // Trim to max length
          const trimmedRaw = newRaw.slice(0, maxLength);

          onChange?.(trimmedRaw);

          if (trimmedRaw.length === maxLength) {
            onComplete?.(trimmedRaw);
          }

          requestAnimationFrame(() => {
            if (inputRef.current) {
              const newFormattedPos = rawToFormattedPosition(
                insertPos + 1,
                mask
              );
              inputRef.current.setSelectionRange(
                newFormattedPos,
                newFormattedPos
              );
            }
          });
        }
      } else {
        // Replace mode - value is already at max length, replace char at cursor
        if (
          insertPos < maxLength &&
          isValidCharAtPosition(char, insertPos, mask)
        ) {
          const newRaw =
            value.slice(0, insertPos) + char + value.slice(insertPos + 1);

          onChange?.(newRaw);

          requestAnimationFrame(() => {
            if (inputRef.current) {
              const newFormattedPos = rawToFormattedPosition(
                insertPos + 1,
                mask
              );
              inputRef.current.setSelectionRange(
                newFormattedPos,
                newFormattedPos
              );
            }
          });
        }
      }
    }

    // Pass through other key handlers
    props.onKeyDown?.(e);
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const input = inputRef.current;
    if (!input) return;

    const pastedText = e.clipboardData.getData('text');
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const maxLength = getMaxRawLength(mask);

    const rawStart = formattedToRawPosition(selectionStart, mask);
    const rawEnd = formattedToRawPosition(selectionEnd, mask);

    // Extract valid characters from pasted text
    let validChars = '';
    let testPos = rawStart;

    for (const char of pastedText) {
      if (testPos >= maxLength) break;
      if (isValidCharAtPosition(char, testPos, mask)) {
        validChars += char;
        testPos++;
      }
    }

    if (validChars.length > 0) {
      const newRaw =
        value.slice(0, rawStart) + validChars + value.slice(rawEnd);
      const trimmedRaw = newRaw.slice(0, maxLength);

      onChange?.(trimmedRaw);

      if (trimmedRaw.length === maxLength) {
        onComplete?.(trimmedRaw);
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          const newFormattedPos = rawToFormattedPosition(
            rawStart + validChars.length,
            mask
          );
          inputRef.current.setSelectionRange(newFormattedPos, newFormattedPos);
        }
      });
    }
  };

  // Prevent default onChange since we handle everything in keyDown
  const handleChange = () => {
    // Intentionally empty - we handle all changes in handleKeyDown
  };

  // Merge refs
  const mergedRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <Input
      {...props}
      ref={mergedRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      placeholder={placeholder || mask.placeholder}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      className={cn(className)}
    />
  );
};
